'use client'

import type { ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { EnterpriseLoginPage } from '@/components/auth/EnterpriseLoginPage'
import { SignUpModal } from '@/components/auth/SignUpModal'
import { MartiniLoader } from '@/components/MartiniLoader'
import { isAnonymousAuthDisabled } from '@/lib/auth'
import { isEnterpriseDeployment } from '@/lib/deployment'
import { stripLocalePrefix } from '@/i18n/surfaces'

/**
 * Sign-in gate for dedicated enterprise builds and deployments without
 * anonymous auth (NEXT_PUBLIC_ANONYMOUS_SIGN_INS_DISABLED=true). Session-less
 * or anonymous visitors get the sign-in flow; consumer deployments with both
 * markers unset render children untouched.
 *
 * /auth/callback is exempt: it must render while the session is still being
 * established (it exchanges the OAuth/SAML redirect and completes conversion).
 */
export function SignedOutGate({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? '/'
  const { auth, isLoading, error, errorKind } = useAuth()
  const canonicalPathname = stripLocalePrefix(pathname).pathname
  const enterpriseDeployment = isEnterpriseDeployment()

  // Public API reference pages render for anyone, on every deployment.
  if (canonicalPathname === '/docs/api' || canonicalPathname === '/docs/workflows') return <>{children}</>

  if (!isAnonymousAuthDisabled() && !enterpriseDeployment) return <>{children}</>

  if (canonicalPathname.startsWith('/auth/callback')) return <>{children}</>

  // Dev-only config diagnostics (Supabase URL mismatch) — let the route's
  // AuthErrorScreen surface the full message. Every other error path (init
  // failure, mid-session invalidation via AUTH_SESSION_INVALID_EVENT) leaves
  // the visitor without a session, so it falls through to the sign-in gate
  // below rather than leaving protected children mounted signed-out.
  if (error && errorKind === 'config') return <>{children}</>

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-2">
        <MartiniLoader className="text-foreground" />
      </div>
    )
  }

  // An anonymous auth state counts as signed out: on an SSO-only deployment a
  // cached anonymous session (from before the rollout, or config drift) must
  // not unlock the protected app. initAuth clears such sessions at boot; this
  // covers any path that surfaces one mid-session.
  if (!auth || auth.isAnonymous) {
    // Preserve query params (e.g. /auth/mcp/authorize carries the OAuth request)
    // so the post-auth return lands on the full original URL. Read lazily —
    // this branch only renders client-side, after auth loading resolves.
    const search = typeof window !== 'undefined' ? window.location.search : ''
    const returnPath = `${canonicalPathname}${search}`

    if (enterpriseDeployment) {
      // Deep links (e.g. /projects) return to themselves post-login; bare
      // root has nothing to return to, so land in the app's home instead.
      return <EnterpriseLoginPage returnPath={canonicalPathname === '/' ? '/projects' : returnPath} />
    }

    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-black px-6">
        <SignUpModal isOpen onClose={() => {}} defaultMode="signin" returnPath={returnPath} />
      </div>
    )
  }

  return <>{children}</>
}
